import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useForm, useFieldArray } from "react-hook-form";
import { zodFormResolver as zodResolver } from "@/lib/zodFormResolver";
import * as z from "zod";
import {
  useGetSnippet,
  useUpdateSnippet,
  useDeleteSnippet,
  useGetSnippetUsage,
  getListSnippetsQueryKey,
  getGetSnippetQueryKey,
  type UpdateSnippetInput,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Loader2, Plus, Trash2, AlertTriangle } from "lucide-react";
import { apiErrorMessage, apiErrorStatus } from "@/lib/apiError";
import { VARIABLE_TYPE_VALUES, VARIABLE_TYPE_LABELS } from "@/lib/enumValues";

const variableSchema = z.object({
  key: z.string().min(1, "Key is required"),
  label: z.string().min(1, "Label is required"),
  type: z.enum(VARIABLE_TYPE_VALUES),
  required: z.boolean(),
  defaultValue: z.string().optional().nullable(),
  placeholder: z.string().optional().nullable(),
});

const formSchema = z.object({
  name: z.string().min(1, "Name is required").max(120),
  description: z.string().max(280),
  bodyHtml: z.string().min(1, "Body HTML is required"),
  variables: z.array(variableSchema),
});

type FormValues = z.infer<typeof formSchema>;

export default function SnippetDetail() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const snippetQuery = useGetSnippet(id);
  const usageQuery = useGetSnippetUsage(id);
  const updateMutation = useUpdateSnippet();
  const deleteMutation = useDeleteSnippet();

  const [deleteOpen, setDeleteOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      bodyHtml: "",
      variables: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "variables",
  });

  // Hydrate the form once the snippet loads. We deliberately don't
  // re-hydrate when the user is mid-edit (form.formState.isDirty)
  // so a background refetch can't blow away typed-but-unsaved input.
  useEffect(() => {
    if (snippetQuery.data && !form.formState.isDirty) {
      form.reset({
        name: snippetQuery.data.name,
        description: snippetQuery.data.description,
        bodyHtml: snippetQuery.data.bodyHtml,
        variables: snippetQuery.data.variables.map((v) => ({
          key: v.key,
          label: v.label,
          type: v.type,
          required: v.required,
          defaultValue: v.defaultValue ?? "",
          placeholder: v.placeholder ?? "",
        })),
      });
    }
  }, [snippetQuery.data, form]);

  if (snippetQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!snippetQuery.data) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Snippet not found.
      </div>
    );
  }

  const snippet = snippetQuery.data;
  const isReadOnly = snippet.isBuiltin;
  const dependents = usageQuery.data?.dependents ?? [];

  const onSubmit = async (values: FormValues) => {
    try {
      const data: UpdateSnippetInput = {
        name: values.name,
        description: values.description,
        bodyHtml: values.bodyHtml,
        variables: values.variables,
      };
      await updateMutation.mutateAsync({ id, data });
      toast.success("Snippet saved");
      queryClient.invalidateQueries({ queryKey: getListSnippetsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetSnippetQueryKey(id) });
      form.reset(values);
    } catch (e: unknown) {
      toast.error(apiErrorMessage(e, "Failed to save snippet"));
    }
  };

  const onDelete = async () => {
    try {
      await deleteMutation.mutateAsync({ id });
      toast.success("Snippet deleted");
      queryClient.invalidateQueries({ queryKey: getListSnippetsQueryKey() });
      setLocation("/app/snippets");
    } catch (e: unknown) {
      // 409 means dependents exist — surface the dependent list inline.
      if (apiErrorStatus(e) === 409) {
        usageQuery.refetch();
        toast.error(
          apiErrorMessage(
            e,
            "This snippet is in use by one or more templates.",
          ),
        );
        setDeleteOpen(false);
      } else {
        toast.error(apiErrorMessage(e, "Failed to delete snippet"));
      }
    }
  };

  return (
    <div
      className="flex-1 overflow-auto p-8 bg-background"
      data-testid="snippet-detail-page"
    >
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/app/snippets")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {snippet.name}
              {snippet.isBuiltin && (
                <Badge variant="secondary">Built-in</Badge>
              )}
            </h1>
            <p className="text-sm text-muted-foreground">
              Insert with{" "}
              <code className="bg-muted px-1 py-0.5 rounded">
                {`{{> ${snippet.slug}}}`}
              </code>
            </p>
          </div>
          {!isReadOnly && (
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="snippet-delete-button"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this snippet?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {dependents.length > 0
                      ? `This snippet is currently used by ${dependents.length} template(s). Deletion will be refused until you remove every reference.`
                      : "This will permanently delete the snippet."}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                {dependents.length > 0 && (
                  <ul className="text-sm border rounded-md p-3 space-y-1 max-h-40 overflow-auto">
                    {dependents.map((d) => (
                      <li key={d.id}>
                        <Link
                          href={`/app/templates/${d.id}`}
                          className="underline"
                        >
                          {d.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDelete}
                    data-testid="snippet-delete-confirm"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {isReadOnly && (
          <div className="rounded-md border bg-muted/40 p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-muted-foreground" />
            <div>
              Built-in snippets are read-only. To customize, create a new
              snippet with the same slug — it will shadow the built-in
              wherever it's referenced.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardContent className="pt-6">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <fieldset disabled={isReadOnly} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input
                              data-testid="snippet-name-input"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Input
                              data-testid="snippet-description-input"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="bodyHtml"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Body HTML</FormLabel>
                          <FormControl>
                            <Textarea
                              rows={14}
                              className="font-mono text-sm"
                              data-testid="snippet-body-input"
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Use <code>{`{{var}}`}</code> placeholders. They
                            resolve against the host template's variable
                            scope at render time. Per-insertion overrides
                            (e.g.{" "}
                            <code>{`{{> ${snippet.slug} year="2026"}}`}</code>
                            ) replace placeholders inline.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <VariablesEditor
                      fields={fields}
                      append={append}
                      remove={remove}
                      form={form}
                    />

                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={
                          updateMutation.isPending || !form.formState.isDirty
                        }
                        data-testid="snippet-save-submit"
                      >
                        {updateMutation.isPending && (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        )}
                        Save changes
                      </Button>
                    </div>
                  </fieldset>
                </form>
              </Form>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Used by {dependents.length} template
                  {dependents.length === 1 ? "" : "s"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {usageQuery.isLoading ? (
                  <div className="text-sm text-muted-foreground">Loading…</div>
                ) : dependents.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    Not referenced by any templates yet. Add{" "}
                    <code>{`{{> ${snippet.slug}}}`}</code> to a template body
                    to see it here.
                  </div>
                ) : (
                  <ul
                    className="text-sm space-y-1"
                    data-testid="snippet-usage-list"
                  >
                    {dependents.map((d) => (
                      <li key={d.id}>
                        <Link
                          href={`/app/templates/${d.id}`}
                          className="underline"
                        >
                          {d.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function VariablesEditor({
  fields,
  append,
  remove,
  form,
}: {
  fields: { id: string }[];
  append: (v: {
    key: string;
    label: string;
    type: "text" | "textarea" | "url" | "email" | "number" | "date";
    required: boolean;
    defaultValue: string | null;
    placeholder: string | null;
  }) => void;
  remove: (idx: number) => void;
  form: ReturnType<typeof useForm<FormValues>>;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Variables</p>
          <p className="text-xs text-muted-foreground">
            Variables here are resolved from the host template's scope.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            append({
              key: "",
              label: "",
              type: "text",
              required: false,
              defaultValue: "",
              placeholder: "",
            })
          }
          data-testid="snippet-add-variable"
        >
          <Plus className="h-4 w-4 mr-1" /> Add variable
        </Button>
      </div>
      {fields.map((f, idx) => (
        <div
          key={f.id}
          className="grid grid-cols-12 gap-2 items-end border rounded-md p-3"
        >
          <FormField
            control={form.control}
            name={`variables.${idx}.key`}
            render={({ field }) => (
              <FormItem className="col-span-3">
                <FormLabel className="text-xs">Key</FormLabel>
                <FormControl>
                  <Input className="h-8" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`variables.${idx}.label`}
            render={({ field }) => (
              <FormItem className="col-span-3">
                <FormLabel className="text-xs">Label</FormLabel>
                <FormControl>
                  <Input className="h-8" {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`variables.${idx}.type`}
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel className="text-xs">Type</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {VARIABLE_TYPE_VALUES.map((v) => (
                      <SelectItem key={v} value={v}>{VARIABLE_TYPE_LABELS[v]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`variables.${idx}.defaultValue`}
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel className="text-xs">Default</FormLabel>
                <FormControl>
                  <Input className="h-8" {...field} value={field.value ?? ""} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`variables.${idx}.required`}
            render={({ field }) => (
              <FormItem className="col-span-1 flex flex-col items-center">
                <FormLabel className="text-xs">Req</FormLabel>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="col-span-1 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => remove(idx)}
              aria-label="Remove variable"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
